// App
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';

// Lodash
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';

// Actions
import UserPointActions from 'app/shared/flux/actions/UserPointActions';

// Components
import CommuteSlider from 'app/shared/modules/MobileSRP/Header/FilterModules/CommuteFilter/CommuteSlider';
import CommuteAutocomplete from 'app/shared/modules/MobileSRP/Header/FilterModules/CommuteFilter/CommuteAutocomplete';
import CommuteButtonDropdown from 'app/shared/modules/MobileSRP/Header/FilterModules/CommuteFilter/CommuteButtonDropdown';

// Hooks
import { useFilterChange } from 'app/shared/modules/filter/FilterBar/hooks';
import {
  useDepartureDropdown,
  useTravelDropdown,
} from 'app/shared/modules/MobileSRP/Header/FilterModules/CommuteFilter/hooks';

// Icons
import IconCar from 'images/icons/car-grey.min.svg';
import IconClock from 'images/icons/clock.min.svg';
import InrixLogo from 'images/inrix-logo.min.svg';
import IconLocationMarker from 'images/location-marker.min.svg';

// Tracking

// Misc / utils
import {
  GeneralFilterHeader,
  GeneralFilterLabel,
  GeneralFilterResetButton,
  GeneralFilterSection,
} from 'app/shared/modules/filter/FilterBar/styles';
import { DEFAULT } from 'app/shared/models/Filter';
import * as S from './styles';

// Types
interface CommuteFilterProps {
  showResetButton: boolean;
}

interface CommuteState {
  commuteLats: number | null;
  commuteLons: number | null;
  commuteMode: string | null;
  commuteTime: number | null;
  commuteTimeMode: string | null;
}

interface UserPoint {
  id: string;
  lat: number;
  lon: number;
  name: string;
  address: string;
  active?: boolean;
}

interface ReduxState {
  filter: {
    commute: CommuteState;
  };
  user: {
    userPoints: {
      activeUserPoint: string | null;
      destinations: Record<string, UserPoint>;
    };
  };
}

const SLIDER_VALUE_TO_MINUTES_MAP = {
  0: null,
  1: 60, // minutes
  2: 45, // minutes
  3: 30, // minutes
  4: 15, // minutes
};

const MINUTES_TO_SLIDER_VALUE_MAP = {
  60: 1,
  45: 2,
  30: 3,
  15: 4,
};

const TRAVEL_MODE_OPTIONS = ['Driving', 'Transit', 'Walking', 'Cycling'];
const DEPARTURE_MODE_OPTIONS = ['Now', 'Rush hour', 'Off peak'];

const CommuteFilter = ({ showResetButton }: CommuteFilterProps) => {
  // Redux
  const dispatch = useDispatch();
  const commuteState = useSelector((state: ReduxState) => state.filter.commute);
  const activeUserPoint = useSelector((state: ReduxState) => state.user.userPoints.activeUserPoint);
  const userPoints = useSelector((state: ReduxState) => state.user.userPoints.destinations);

  // React refs
  const travelButtonRef = useRef<HTMLButtonElement>(null);
  const departureButtonRef = useRef<HTMLButtonElement>(null);
  const travelModeOptionsRefList = TRAVEL_MODE_OPTIONS.map(() => React.createRef<HTMLLIElement>());
  const departureModeOptionsRefList = DEPARTURE_MODE_OPTIONS.map(() => React.createRef<HTMLLIElement>());

  // React state
  const [isLoaded, setIsLoaded] = useState(false);
  const [isAddressLoaded, setIsAddressLoaded] = useState(true);
  const [filterState, setFilterState] = useState(commuteState);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const [showTravelDropdown, setTravelDropdown] = useState(false);
  const [showDepartureDropdown, setDepartureDropdown] = useState(false);
  const [inputValue, setInputValue] = useState('');
  let userPoint: UserPoint | undefined;

  if (!isEmpty(userPoints) && !isNull(activeUserPoint)) {
    userPoint = userPoints[activeUserPoint];
  }

  // Hooks
  const { reduxOnlyChange, resetFilter, isFilterResetting } = useFilterChange();

  const {
    handleTravelModeButtonClick,
    handleTravelModeButtonKeyDown,
    handleTravelModeOptionClick,
    handleTravelModeOptionKeyDown,
  } = useTravelDropdown({
    focusRef: travelButtonRef,
    refList: travelModeOptionsRefList,
    showDropdown: showTravelDropdown,
    setShowDropdown: setTravelDropdown,
    toggleDepartureDropdown: setDepartureDropdown,
    filterState,
    setFilterState,
  });
  const {
    handleDepartureButtonClick,
    handleDepartureButtonKeyDown,
    handleDepartureOptionClick,
    handleDepartureOptionKeyDown,
  } = useDepartureDropdown({
    focusRef: departureButtonRef,
    refList: departureModeOptionsRefList,
    showDropdown: showDepartureDropdown,
    setShowDropdown: setDepartureDropdown,
    toggleTravelModeDropdown: setTravelDropdown,
    filterState,
    setFilterState,
  });

  useEffect(() => {
    if (!isLoaded) {
      const isFilterStateEmptyButUserPointExists =
        filterState.commuteTime === null &&
        filterState.commuteMode === null &&
        filterState.commuteTimeMode === null &&
        userPoint;
      if (isFilterStateEmptyButUserPointExists && userPoint) {
        setFilterState({
          commuteLats: userPoint.lat,
          commuteLons: userPoint.lon,
          commuteMode: 'Driving',
          commuteTime: null,
          commuteTimeMode: 'Now',
        });
      }
      setIsLoaded(true);
    } else {
      if (userPoint && userPoint.lat && userPoint.lon && !isEmpty(filterState) && !isFilterResetting) {
        reduxOnlyChange({ commute: filterState });
      }
    }
  }, [filterState, userPoint, isLoaded, isFilterResetting, reduxOnlyChange]);

  const handleResetClick = useYieldCallback(() => {
    resetFilter({ commute: DEFAULT.commute });
    setFilterState({
      commuteLats: null,
      commuteLons: null,
      commuteMode: null,
      commuteTimeMode: null,
      commuteTime: null,
    });
    setInputValue('');
    // Force show autocomplete after reset even if userPoint exists
    setShowAddressDropdown(true);
  });

  const handleRemoveClick = useYieldCallback(() => {
    resetFilter({ commute: DEFAULT.commute });
    setFilterState({
      commuteLats: null,
      commuteLons: null,
      commuteMode: null,
      commuteTimeMode: null,
      commuteTime: null,
    });
    setInputValue('');
    // Deselect the active userPoint by passing a non-existent address
    dispatch(UserPointActions.select(''));
    // Don't force autocomplete to show - just remove the destination
    setShowAddressDropdown(false);
  });

  // ADDRESS FIELD
  const handleAddressEditClick = () => {
    setShowAddressDropdown(true);
    setInputValue('');
  };

  const handleAddressChange = ({ lat, lon }: { lat: number; lon: number }) => {
    // Commute filters depend on having an address! So if this is hit... update to new defaults
    const commuteMode = filterState.commuteMode;
    const commuteTime = filterState.commuteTime;
    const commuteTimeMode = filterState.commuteTimeMode;
    const setNewDefaults = commuteMode === null && commuteTime === null && commuteTimeMode === null;

    if (setNewDefaults) {
      setFilterState({
        commuteLats: lat,
        commuteLons: lon,
        commuteMode: 'Driving',
        commuteTime: null,
        commuteTimeMode: 'Now',
      });
    } else {
      setFilterState({
        ...filterState,
        commuteLats: lat,
        commuteLons: lon,
      });
    }
  };

  // SLIDER FIELD
  const handleSliderSelection = (val: number) => {
    const commuteTime = SLIDER_VALUE_TO_MINUTES_MAP[val as keyof typeof SLIDER_VALUE_TO_MINUTES_MAP];
    setFilterState({
      ...filterState,
      commuteTime,
    });
  };

  return (
    <GeneralFilterSection>
      <GeneralFilterHeader>
        <GeneralFilterLabel>
          Commute <S.InrixLogo src={InrixLogo} alt="icon of Inrix logo" height="12px" width="48px" loading="lazy" />
        </GeneralFilterLabel>
        {showResetButton && (
          <GeneralFilterResetButton onClick={handleResetClick} aria-label={'Reset all active commute filters'}>
            Reset
          </GeneralFilterResetButton>
        )}
      </GeneralFilterHeader>

      <S.EditCommuteDestinationHeader>
        <S.EditCommuteText htmlFor="commute search box">Edit commute destination</S.EditCommuteText>
        {!showAddressDropdown && userPoint && (
          <S.CommuteAddressActionsContainer>
            <S.CommuteAddressEditButton
              buttonType="text"
              onClick={handleAddressEditClick}
              aria-label={`Edit your commute destination, which is currently ${userPoint.name}`}
            >
              Edit
            </S.CommuteAddressEditButton>
            <S.CommuteAddressRemoveButton
              buttonType="text"
              onClick={handleRemoveClick}
              aria-label={`Remove commute destination ${userPoint.name}`}
            >
              Remove
            </S.CommuteAddressRemoveButton>
          </S.CommuteAddressActionsContainer>
        )}
      </S.EditCommuteDestinationHeader>
      {showAddressDropdown || !userPoint ? (
        <CommuteAutocomplete
          showDropdown={showAddressDropdown}
          setShowDropdown={setShowAddressDropdown}
          setIsAddressLoaded={setIsAddressLoaded}
          handleAddressChange={handleAddressChange}
          userPoint={userPoint ?? null}
          inputValue={inputValue}
          onInputChange={setInputValue}
        />
      ) : (
        <S.CommuteAddressFlexContainer>
          <S.CommuteAddressLocationIconContainer aria-hidden>
            <S.CommuteAddressLocationIcon src={IconLocationMarker} width="16px" height="16px" alt="" loading="lazy" />
          </S.CommuteAddressLocationIconContainer>
          <p>{userPoint.name}</p>
        </S.CommuteAddressFlexContainer>
      )}
      <S.DropdownFlexContainer>
        <S.DropdownFlexSection>
          <S.DropdownLabel htmlFor="travel-mode-button-dropdown">
            <S.DropdownLabelIcon src={IconCar} height="14px" width="14px" alt="" loading="lazy" />
            Travel mode
          </S.DropdownLabel>
          <CommuteButtonDropdown
            ariaLabel={`Select your commute travel mode, the current selected travel mode is ${
              filterState.commuteMode || 'Driving'
            }`}
            buttonRef={travelButtonRef}
            currentButtonValue={filterState.commuteMode || 'Driving'}
            dropDownItemClick={handleTravelModeOptionClick}
            dropDownItemKeyDown={handleTravelModeOptionKeyDown}
            dropDownItemRefList={travelModeOptionsRefList}
            isButtonDisabled={!isAddressLoaded}
            labelId="travel-mode-button-dropdown"
            onButtonClick={handleTravelModeButtonClick}
            onButtonKeyDown={handleTravelModeButtonKeyDown}
            options={TRAVEL_MODE_OPTIONS}
            showDropdown={showTravelDropdown}
          />
        </S.DropdownFlexSection>
        <S.DropdownFlexSection>
          <S.DropdownLabel htmlFor="time-of-day-button-dropdown">
            <S.DropdownLabelIcon src={IconClock} height="14px" width="14px" alt="" loading="lazy" />
            Time of day
          </S.DropdownLabel>
          <CommuteButtonDropdown
            ariaLabel={`Select your commute time of day, the current selected time of day is ${
              filterState.commuteTimeMode || 'Now'
            }`}
            buttonRef={departureButtonRef}
            currentButtonValue={filterState.commuteTimeMode || 'Now'}
            dropDownItemClick={handleDepartureOptionClick}
            dropDownItemKeyDown={handleDepartureOptionKeyDown}
            dropDownItemRefList={departureModeOptionsRefList}
            isButtonDisabled={!isAddressLoaded}
            labelId="time-of-day-button-dropdown"
            onButtonClick={handleDepartureButtonClick}
            onButtonKeyDown={handleDepartureButtonKeyDown}
            options={DEPARTURE_MODE_OPTIONS}
            showDropdown={showDepartureDropdown}
          />
        </S.DropdownFlexSection>
      </S.DropdownFlexContainer>
      <CommuteSlider
        handleSliderSelection={handleSliderSelection}
        currentValue={
          filterState.commuteTime !== null
            ? (MINUTES_TO_SLIDER_VALUE_MAP[filterState.commuteTime as keyof typeof MINUTES_TO_SLIDER_VALUE_MAP] ?? 0)
            : 0
        }
      />
    </GeneralFilterSection>
  );
};

export default CommuteFilter;
